// SVG-based health-score gauge. Pure presentational — takes a 0-100 score
// + a §6.3.5 color band and draws a 270° arc filled to the score. Pure SVG
// (no Recharts) because Recharts' RadialBarChart drags in the full chart
// runtime for one arc + one label, which would cost more on the network
// than the entire rest of the page.

import type { CommunityHealthColor } from "@community-analytics/shared";

import { cn } from "@shared/lib";

import { healthStrokeColor } from "../lib/format";

export interface HealthGaugeProps {
  /** 0–100; out-of-range values are clamped. */
  score: number;
  label: string;
  color: CommunityHealthColor;
  /** Optional caption rendered below the score (e.g. "Last updated 2 min ago"). */
  caption?: string;
  className?: string;
}

const SIZE = 160;
const STROKE = 14;
const RADIUS = (SIZE - STROKE) / 2;
const ARC_DEGREES = 270;
// Arc length the stroke covers when the score = 100.
const FULL_ARC = (Math.PI * 2 * RADIUS * ARC_DEGREES) / 360;

export function HealthGauge({
  score,
  label,
  color,
  caption,
  className,
}: HealthGaugeProps) {
  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  const filled = (clamped / 100) * FULL_ARC;
  const stroke = healthStrokeColor(color);

  // SVG circumference; the dasharray draws a partial arc with the fill arc
  // first, then a gap that hides the rest. Rotated -135° so the arc opens
  // at the bottom (a 270° gauge with the gap centered on south).
  const circumference = Math.PI * 2 * RADIUS;
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-2",
        className,
      )}
      role="img"
      aria-label={`${label} (${clamped} of 100)`}
    >
      <div className="relative" style={{ width: SIZE, height: SIZE }}>
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="-rotate-[135deg]"
        >
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="hsl(var(--muted))"
            strokeWidth={STROKE}
            strokeDasharray={`${FULL_ARC} ${circumference}`}
            strokeLinecap="round"
          />
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke={stroke}
            strokeWidth={STROKE}
            strokeDasharray={`${filled} ${circumference}`}
            strokeLinecap="round"
            style={{ transition: "stroke-dasharray 400ms ease-out" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-4xl font-bold tabular-nums">{clamped}</div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {label}
          </div>
        </div>
      </div>
      {caption ? (
        <div className="text-xs text-muted-foreground">{caption}</div>
      ) : null}
    </div>
  );
}
